import * as React from 'react'
import { saveWithInvoiceClosure } from './invoiceApi'
import { supabase } from './supabase'
import { Button, AdminField, Dialog } from './ui'
import { addDays, moneyInput, parsePence, type OpsExtension } from './operationsModel'

type Booking = {
  source: 'manual' | 'online'
  source_id: string
  reference: string
  effective_end_date: string
  return_date: string
  updated_at: string
}

function extensionError(error: unknown) {
  const text = error && typeof error === 'object' && 'message' in error ? String(error.message) : String(error)
  const messages: Record<string, string> = {
    UNIT_UNAVAILABLE: 'An assigned unit is unavailable for the extended dates. Change the units or choose different dates.',
    UNITS_MUST_MATCH_BOOKED_CLASS: 'An assigned unit is no longer active. Change the assigned units before extending this booking.',
    COLLECTION_DATE_BLOCKED: 'Collection is unavailable on this date. Choose another return date.',
    BOOKING_CHANGED_REFRESH: 'This booking has changed. Close this form and refresh the booking before trying again.',
    RETURN_BEFORE_HIRE_END: 'The return date must be on or after the extended hire end.',
    EXTENSION_MUST_PRECEDE_NEXT_END: 'This extension must end before the following extension.',
    EXTENSION_NOT_FOUND: 'This extension no longer exists. Close this form and refresh the booking.',
    INVOICE_BUSY: 'An invoice operation is still running. Wait for it to finish, then try deleting again.',
    INVOICE_LINK_OPEN: 'An invoice payment link is still open. Try deleting again to close it first.',
    BOOKING_NOT_OPEN: 'This booking is no longer open for extensions.',
    PAID_BOOKING_REQUIRED: 'This booking can no longer be extended. Check its payment status.',
  }
  return Object.entries(messages).find(([code]) => text.includes(code))?.[1] || text
}

export function BookingExtensionForm({ booking, extension, onClose, onSaved }: {
  booking: Booking
  extension?: OpsExtension
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [form, setForm] = React.useState({
    new_end_date: extension?.new_end_date || addDays(booking.effective_end_date, 7),
    return_date: extension ? booking.return_date : addDays(booking.return_date, 7),
    payment_status: extension?.payment_status || 'unpaid', value: moneyInput(extension?.value_pence),
    invoice_url: extension?.invoice_url || '', notes: extension?.notes || '',
  })
  const [pending, setPending] = React.useState<'save' | 'delete' | null>(null)
  const busy = pending !== null
  const [error, setError] = React.useState('')
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy) return
    setPending('save')
    setError('')
    try {
      await saveWithInvoiceClosure(async () => {
      const { error } = await supabase.rpc(booking.source === 'online' ? 'admin_save_live_extension' : 'admin_ops_save_extension', {
        p_booking_id: booking.source_id, p_extension: { ...form, value_pence: parsePence(form.value) },
        p_return_date: form.return_date, p_expected_updated_at: booking.updated_at, p_id: extension?.id || null,
      })
      if (error) throw error
      }, { kind: 'extension', id: extension?.id || '' })
      await onSaved()
    } catch (error) {
      setError(extensionError(error))
    } finally {
      setPending(null)
    }
  }
  const remove = async () => {
    if (!extension || busy) return
    if (!window.confirm(`Delete this extension for ${booking.reference}? This removes its recorded value and recalculates the hire end. Deleting the latest extension also restores the previous return date. Generated payment links will be closed and invoice history retained. This cannot be undone and does not issue a refund.`)) return
    setPending('delete')
    setError('')
    try {
      await saveWithInvoiceClosure(async () => {
        const { error } = await supabase.rpc('admin_delete_booking_extension', {
          p_booking_id: booking.source_id, p_source: booking.source,
          p_id: extension.id, p_expected_updated_at: booking.updated_at,
        })
        if (error) throw error
      }, { kind: 'extension', id: extension.id })
      await onSaved()
    } catch (error) {
      setError(extensionError(error))
    } finally {
      setPending(null)
    }
  }
  return <Dialog isOpen onOpenChange={open => { if (!open && !busy) onClose() }} purpose="form" width="min(850px, 96vw)" maxHeight="92dvh">
    <form className="ops-dialog" onSubmit={event => void save(event)}>
      <h2>{extension ? 'Edit' : 'Add'} extension · {booking.reference}</h2>
      {booking.source === 'online' && <p>Record an extension agreed with the customer. Payment is tracked manually; saving does not charge Stripe or change the original checkout order. Assigned equipment and the collection date must be available.</p>}
      <fieldset disabled={busy}>
        <div className="ops-grid">
          <AdminField>Extension end<input required type="date" min={addDays(extension?.previous_end_date || booking.effective_end_date, 1)} value={form.new_end_date} onChange={e => setForm({ ...form, new_end_date: e.target.value })} /></AdminField>
          <AdminField>Return date<input required type="date" min={form.new_end_date} value={form.return_date} onChange={e => setForm({ ...form, return_date: e.target.value })} /></AdminField>
          <AdminField>Value (£)<input inputMode="decimal" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} /></AdminField>
          <AdminField>Payment<select value={form.payment_status} onChange={e => setForm({ ...form, payment_status: e.target.value })}><option>unpaid</option><option>paid</option></select></AdminField>
        </div>
        <AdminField>Invoice link<input type="url" value={form.invoice_url} onChange={e => setForm({ ...form, invoice_url: e.target.value })} /></AdminField>
        <AdminField>Notes<textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></AdminField>
      </fieldset>
      {error && <p className="message error" role="alert">{error}</p>}
      <div className="ops-actions">
        <Button type="button" disabled={busy} onClick={onClose}>Cancel</Button>
        <Button className="primary" disabled={busy}>{pending === 'save' ? 'Saving…' : 'Save'}</Button>
        {extension && <Button type="button" className="danger" disabled={busy} onClick={() => void remove()}>{pending === 'delete' ? 'Deleting…' : 'Delete extension'}</Button>}
      </div>
    </form>
  </Dialog>
}
